import { type SectionType } from "@profolio/validators";

export type CVTemplateId = 
  | "academic-classic"
  | "modern-executive"
  | "minimal-clean"
  | "compact-two-column";

export type CVFontFamily = "times" | "helvetica";
export type CVPageSize = "A4" | "LETTER";

export interface CVColorPreset {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  border: string;
  badgeBg: string;
}

export const CV_COLOR_PRESETS: CVColorPreset[] = [
  {
    id: "navy",
    name: "Oxford Navy",
    primary: "#1e3a8a",
    secondary: "#3b82f6",
    border: "#cbd5e1",
    badgeBg: "#eff6ff",
  },
  {
    id: "slate",
    name: "Slate Charcoal",
    primary: "#1e293b",
    secondary: "#475569",
    border: "#cbd5e1",
    badgeBg: "#f1f5f9",
  },
  {
    id: "burgundy",
    name: "Cambridge Burgundy",
    primary: "#881337",
    secondary: "#be123c",
    border: "#fecdd3",
    badgeBg: "#fff1f2",
  },
  {
    id: "forest",
    name: "Dartmouth Forest",
    primary: "#14532d",
    secondary: "#15803d",
    border: "#bbf7d0",
    badgeBg: "#f0fdf4",
  },
  {
    id: "royal",
    name: "Imperial Royal",
    primary: "#3730a3",
    secondary: "#4f46e5",
    border: "#c7d2fe",
    badgeBg: "#eef2ff",
  },
  {
    id: "teal",
    name: "Nordic Teal",
    primary: "#0f766e",
    secondary: "#0d9488",
    border: "#99f6e4",
    badgeBg: "#f0fdfa",
  },
  {
    id: "charcoal",
    name: "Monochrome",
    primary: "#18181b",
    secondary: "#52525b",
    border: "#d4d4d8",
    badgeBg: "#f4f4f5",
  },
];

export interface CVSettings {
  template: CVTemplateId;
  fontFamily: CVFontFamily;
  accentColor: string;
  pageSize: CVPageSize;
  
  // Contact detail toggles
  showEmail: boolean;
  showPhone: boolean;
  showLocation: boolean;
  showWebsite: boolean;
  showScholar: boolean;
  showOrcid: boolean;
  showLinkedin: boolean;
  showGithub: boolean;
  
  // Section content detail toggles
  showAbstracts: boolean;
  showGrantsAmount: boolean;
  showCourseCodes: boolean;
  showPublicationBadges: boolean;
  
  // Academic formatting toggles
  citationStyle: "numbered" | "standard";
  density: "standard" | "compact";
  showCvHeaderBanner: boolean;
  
  // Section ordering & visibility
  includedSectionIds: string[];
  customSectionTitles: Record<string, string>;
}

export const DEFAULT_CV_SETTINGS: CVSettings = {
  template: "academic-classic",
  fontFamily: "times",
  accentColor: "#1e3a8a",
  pageSize: "A4",
  
  showEmail: true,
  showPhone: true,
  showLocation: true,
  showWebsite: true,
  showScholar: true,
  showOrcid: true,
  showLinkedin: true,
  showGithub: true,
  
  showAbstracts: false,
  showGrantsAmount: true,
  showCourseCodes: true,
  showPublicationBadges: true,
  
  citationStyle: "numbered",
  density: "standard",
  showCvHeaderBanner: true,
  
  includedSectionIds: [],
  customSectionTitles: {},
};

export interface CandidateContact {
  email?: string;
  phone?: string;
  office?: string;
  address?: string;
  location?: string;
  websiteUrl?: string;
  googleScholarUrl?: string;
  orcidUrl?: string;
  researchGateUrl?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  twitterUrl?: string;
  socialLinks: Array<{ platform: string; url: string; label?: string }>;
}

export interface CandidateInfo {
  fullName: string;
  title?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
  subdomain?: string | null;
  contact: CandidateContact;
}

export interface RawSection {
  id: string;
  type: SectionType;
  content?: any;
  visible: boolean;
  displayOrder?: number;
}
